/**
 * CorporateShell · shared chrome for every /:slug/* page after sign-in.
 *
 * Sticky top bar with:
 *   • left  · {Company text mark} × Les Concierges
 *   • right · Signed in as {First} · {Avatar} · SIGN OUT
 *
 * Body wraps children in the LC cream canvas.
 * Kept as a thin wrapper so pages control their own vertical rhythm.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { corporateApi } from "@/lib/corporate-api";

const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
};

export default function CorporateShell({
  tenant,
  member,
  children,
  showSignOut = true,
}) {
  const navigate = useNavigate();
  const initial = ((member?.first_name || member?.email || "?")[0] || "?").toUpperCase();
  const displayName =
    (member?.first_name && `${member.first_name}${member.last_name ? " " + member.last_name : ""}`) ||
    (member?.email || "");

  async function onSignOut() {
    try { await corporateApi.signOut(); } catch { /* swallow */ }
    if (tenant?.slug) navigate(`/${tenant.slug}`);
    else navigate("/");
  }

  return (
    <div
      data-testid="corp-shell"
      style={{ minHeight: "100vh", background: T.bg, color: T.ink,
               overflowX: "hidden",
               fontFamily: "'Hanken Grotesk', system-ui, sans-serif" }}
    >
      {/* Header */}
      <header
        data-testid="corp-shell-header"
        style={{
          position: "sticky", top: 0, zIndex: 40,
          background: `${T.bg}f0`, backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${T.line}`,
          padding: "12px 16px", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 12,
          flexWrap: "wrap",
        }}
      >
        {/* Left · company mark × LC */}
        <div
          data-testid="corp-shell-brand"
          onClick={() => navigate(tenant?.slug ? `/${tenant.slug}/dashboard` : "/")}
          style={{ display: "flex", alignItems: "center", gap: 10,
                   cursor: "pointer", minWidth: 0, flex: "1 1 auto" }}
        >
          <span
            data-testid="corp-shell-company-mark"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 600, fontSize: "clamp(14px, 3.6vw, 20px)",
              color: T.brass,
              letterSpacing: "0.06em", textTransform: "uppercase",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: "58vw",
            }}
          >
            {(tenant?.display_name || tenant?.slug || "").toUpperCase()}
          </span>
          <span style={{ color: T.ink3, fontSize: 14, flexShrink: 0 }}>×</span>
          <span
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 500, fontSize: "clamp(12px, 3vw, 15px)", color: T.ink,
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
            }}
          >
            Les Concierges
          </span>
        </div>

        {/* Right · signed in / sign out */}
        {member && showSignOut ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10,
                        flexShrink: 0 }}>
            <div style={{ textAlign: "right", lineHeight: 1.2,
                          display: "none" }}
                 className="corp-shell-signed-in-block">
              {/* Hidden on mobile via CSS below · shown ≥560px */}
              <div style={{ fontSize: 10.5, letterSpacing: "0.14em",
                            textTransform: "uppercase", color: T.ink3 }}>
                Signed in as
              </div>
              <div
                data-testid="corp-shell-member-name"
                style={{ fontSize: 14, color: T.ink, fontWeight: 500,
                         whiteSpace: "nowrap", maxWidth: "38vw",
                         overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {displayName}
              </div>
            </div>
            <div
              aria-hidden
              title={displayName}
              style={{
                width: 34, height: 34, borderRadius: "50%",
                background: T.brass, color: T.bg, display: "flex",
                alignItems: "center", justifyContent: "center",
                fontFamily: "'Fraunces', Georgia, serif",
                fontWeight: 600, fontSize: 15, flexShrink: 0,
              }}
            >
              {initial}
            </div>
            <button
              data-testid="corp-shell-sign-out"
              type="button"
              onClick={onSignOut}
              style={{
                background: "transparent", color: T.ink2,
                border: `1px solid ${T.line2}`, borderRadius: 999,
                padding: "7px 14px", fontSize: 11, fontWeight: 600,
                letterSpacing: "0.08em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "inherit",
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              Sign out
            </button>
          </div>
        ) : null}
        {/* Reveal the "SIGNED IN AS" label + name only from 560px up */}
        <style>{`
          @media (min-width: 560px) {
            [data-testid="corp-shell-header"] .corp-shell-signed-in-block {
              display: block !important;
            }
          }
        `}</style>
      </header>

      <main style={{ maxWidth: 1180, margin: "0 auto",
                     padding: "24px 16px 64px",
                     minWidth: 0 }}>
        {children}
      </main>

      <footer style={{ borderTop: `1px solid ${T.line}`, padding: "24px",
                       textAlign: "center", color: T.ink3, fontSize: 12 }}>
        Sponsored benefit · Powered by Les Concierges · Fulfilment by our
        live Concierge® team.
      </footer>
    </div>
  );
}
